import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bug, X, Volume2, Play, Pause } from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'

const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { 
    currentTrack, 
    isPlaying, 
    currentTime, 
    duration, 
    volume,
    playlist 
  } = useMusic()

  // 在开发环境中显示
  if (process.env.NODE_ENV !== 'development') return null

  return (
    <>
      {/* 调试按钮 */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 p-3 bg-gray-800 text-white rounded-full shadow-lg z-50"
      >
        <Bug className="w-5 h-5" />
      </motion.button>

      {/* 调试面板 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed bottom-20 right-4 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50"
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">调试信息</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                {/* 当前曲目 */}
                <div>
                  <div className="font-medium text-gray-700">当前曲目:</div>
                  <div className="text-gray-600 pl-2">
                    {currentTrack ? (
                      <div>
                        <div>📻 {currentTrack.title}</div>
                        <div>👤 {currentTrack.artist}</div>
                        <div className="text-xs text-blue-600 break-all">
                          🔗 {currentTrack.audioUrl}
                        </div>
                      </div>
                    ) : (
                      '无'
                    )}
                  </div>
                </div>

                {/* 播放状态 */}
                <div>
                  <div className="font-medium text-gray-700">播放状态:</div>
                  <div className="text-gray-600 pl-2 flex items-center space-x-2">
                    {isPlaying ? (
                      <>
                        <Pause className="w-4 h-4 text-green-600" />
                        <span>播放中</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 text-gray-600" />
                        <span>暂停</span>
                      </>
                    )}
                  </div>
                </div>

                {/* 播放进度 */}
                <div>
                  <div className="font-medium text-gray-700">播放进度:</div>
                  <div className="text-gray-600 pl-2">
                    ⏱️ {Math.floor(currentTime)}s / {Math.floor(duration)}s
                  </div>
                </div>

                {/* 音量 */}
                <div>
                  <div className="font-medium text-gray-700">音量:</div>
                  <div className="text-gray-600 pl-2 flex items-center space-x-2">
                    <Volume2 className="w-4 h-4" />
                    <span>{Math.round(volume * 100)}%</span>
                  </div>
                </div>

                {/* 播放列表 */}
                <div>
                  <div className="font-medium text-gray-700">播放列表:</div>
                  <div className="text-gray-600 pl-2">
                    📝 {playlist.length} 首歌曲
                  </div>
                </div>

                {/* 浏览器信息 */}
                <div>
                  <div className="font-medium text-gray-700">浏览器支持:</div>
                  <div className="text-gray-600 pl-2 text-xs">
                    <div>🎵 Audio API: {window.Audio ? '✅' : '❌'}</div>
                    <div>🎧 MP3: {new Audio().canPlayType('audio/mpeg') ? '✅' : '❌'}</div>
                    <div>🎶 OGG: {new Audio().canPlayType('audio/ogg') ? '✅' : '❌'}</div>
                    <div>🔊 WAV: {new Audio().canPlayType('audio/wav') ? '✅' : '❌'}</div>
                    <div>📱 MP4: {new Audio().canPlayType('audio/mp4') ? '✅' : '❌'}</div>
                    <div>🌐 User Agent: {navigator.userAgent.split(' ')[0]}</div>
                    <div>🔒 Protocol: {location.protocol}</div>
                  </div>
                </div>

                {/* 测试按钮 */}
                <div className="pt-3 border-t border-gray-100">
                  <button
                    onClick={() => {
                      const audio = new Audio('https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3')
                      audio.play()
                        .then(() => console.log('测试音频播放成功'))
                        .catch(err => console.error('测试音频播放失败:', err))
                    }}
                    className="w-full py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                  >
                    🧪 测试音频播放
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default DebugPanel
